import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { FileText, Download, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomEmployeeReport = () => {
  const { t } = useTranslation();
  const { dataVersion } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Field selection state
  const [selectedFields, setSelectedFields] = useState({
    // Basic Info
    name: true,
    email: true,
    phone: true,
    url: false,
    status: true,
    department: true,
    position_1: true,
    position_2: false,
    position_3: false,
    country: true,
    address: false,
    
    // Employment
    start_date: true,
    hire_date: false,
    employee_code: false,
    
    // Rates
    hourly_rate_1: true,
    currency_1: true,
    hourly_rate_2: false,
    currency_2: false,
    hourly_rate_3: false,
    currency_3: false,
    
    // Personal
    birth_date: false,
    birth_place: false,
    personal_id_number: false,
    id_card_number: false,
    id_card_expiry_date: false,
    passport_number: false,
    passport_expiry_date: false,
    
    // Emergency Contact
    emergency_contact_name: false,
    emergency_contact_phone: false,
    emergency_contact_email: false,
    emergency_contact_relationship: false,
    
    // Business
    business_name: false,
    tax_id_number: false,
    pkd_number: false,
    
    // Visas
    eu_visa_type: false,
    eu_visa_expiry_date: false,
    us_visa_type: false,
    us_visa_expiry_date: false,
    uk_work_permit_type: false,
    uk_work_permit_expiry_date: false,
    
    // Certificates (summary)
    certificates: false
  });
  
  // Filter state
  const [filters, setFilters] = useState({
    status: 'all',
    department: 'all',
    selectedEmployees: [] // Array of employee IDs
  });
  
  const [departments, setDepartments] = useState([]);
  const [selectAllEmployees, setSelectAllEmployees] = useState(true);

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, [dataVersion]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(t('auto.misc.failedToLoadEmployees'));
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleFieldToggle = (field) => {
    setSelectedFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.keys(selectedFields).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setSelectedFields(allSelected);
  };

  const handleSelectNone = () => {
    const noneSelected = Object.keys(selectedFields).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});
    setSelectedFields(noneSelected);
  };

  const handleGroupToggle = (groupFields) => {
    // Check if all fields in group are selected
    const allSelected = groupFields.every(field => selectedFields[field.key]);
    
    // Toggle all fields in the group
    const updated = { ...selectedFields };
    groupFields.forEach(field => {
      updated[field.key] = !allSelected;
    });
    setSelectedFields(updated);
  };

  const isGroupSelected = (groupFields) => {
    return groupFields.every(field => selectedFields[field.key]);
  };

  const handleEmployeeToggle = (employeeId) => {
    setFilters(prev => {
      // When the user is currently in "all selected implicitly" mode
      // (selectAllEmployees === true and selectedEmployees is empty), we
      // must first expand that implicit selection into an explicit list so
      // clicking one checkbox only unchecks that one — matching what the
      // user visually sees.
      let currentSelection = prev.selectedEmployees;
      if (selectAllEmployees && currentSelection.length === 0) {
        currentSelection = getBaseFilteredEmployees().map(e => e.id);
      }
      const selected = currentSelection.includes(employeeId)
        ? currentSelection.filter(id => id !== employeeId)
        : [...currentSelection, employeeId];
      return { ...prev, selectedEmployees: selected };
    });
    // Any manual click drops the implicit "select all" state.
    setSelectAllEmployees(false);
  };

  const handleSelectAllEmployeesToggle = () => {
    if (selectAllEmployees) {
      // Deselect all
      setFilters(prev => ({ ...prev, selectedEmployees: [] }));
      setSelectAllEmployees(false);
    } else {
      // Select all filtered employees
      const filtered = getBaseFilteredEmployees();
      setFilters(prev => ({ ...prev, selectedEmployees: filtered.map(e => e.id) }));
      setSelectAllEmployees(true);
    }
  };

  const getBaseFilteredEmployees = () => {
    return employees.filter(emp => {
      if (filters.status !== 'all' && emp.status !== filters.status) {
        return false;
      }
      if (filters.department !== 'all' && emp.department !== filters.department) {
        return false;
      }
      return true;
    });
  };

  const getFilteredEmployees = () => {
    const baseFiltered = getBaseFilteredEmployees();

    // If specific employees are selected, filter by those.
    if (filters.selectedEmployees.length > 0) {
      return baseFiltered.filter(emp => filters.selectedEmployees.includes(emp.id));
    }

    // No explicit selection: honor the implicit "select all" flag.
    // If the user explicitly clicked "Deselect All" (selectAllEmployees=false)
    // and hasn't picked anyone individually, treat that as an empty report so
    // the "No employees match" guard triggers rather than silently returning
    // everyone.
    return selectAllEmployees ? baseFiltered : [];
  };

  const generateReport = async (format = 'xlsx') => {
    setGenerating(true);
    
    try {
      const filteredEmployees = getFilteredEmployees();
      
      if (filteredEmployees.length === 0) {
        toast.error(t('auto.misc.noEmployeesMatchTheSelectedFilters'));
        return;
      }

      const response = await axios.post(`${API}/reports/employees/custom`, {
        fields: selectedFields,
        filters: filters,
        format: format
      }, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', weekFileName(`custom_employee_report_${new Date().toISOString().split('T')[0]}.xlsx`));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('auto.misc.excelReportDownloadedSuccessfully'));
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(t('auto.misc.failedToGenerateReport'));
    } finally {
      setGenerating(false);
    }
  };

  const fieldGroups = [
    {
      title: t('auto.lbl.basicInformation'),
      fields: [
        { key: 'name', label: t('auto.lbl.fullName') },
        { key: 'email', label: t('auto.lbl.email') },
        { key: 'phone', label: t('auto.lbl.phone') },
        { key: 'url', label: t('auto.lbl.webUrl') },
        { key: 'status', label: t('auto.lbl.status') },
        { key: 'department', label: t('auto.lbl.department') },
        { key: 'position_1', label: t('auto.lbl.position1') },
        { key: 'position_2', label: t('auto.lbl.position2') },
        { key: 'position_3', label: t('auto.lbl.position3') },
        { key: 'country', label: t('auto.lbl.country') },
        { key: 'address', label: t('auto.lbl.address') }
      ]
    },
    {
      title: t('auto.lbl.employmentDetails'),
      fields: [
        { key: 'start_date', label: t('auto.lbl.startDate') },
        { key: 'hire_date', label: t('auto.lbl.hireDate') },
        { key: 'employee_code', label: t('auto.lbl.employeeCode') }
      ]
    },
    {
      title: t('auto.lbl.hourlyRates'),
      fields: [
        { key: 'hourly_rate_1', label: t('auto.lbl.hourlyRate1') },
        { key: 'currency_1', label: t('auto.lbl.currency1') },
        { key: 'hourly_rate_2', label: t('auto.lbl.hourlyRate2') },
        { key: 'currency_2', label: t('auto.lbl.currency2') },
        { key: 'hourly_rate_3', label: t('auto.lbl.hourlyRate3') },
        { key: 'currency_3', label: t('auto.lbl.currency3') }
      ]
    },
    {
      title: t('auto.lbl.personalInformation'),
      fields: [
        { key: 'birth_date', label: t('auto.lbl.birthDate') },
        { key: 'birth_place', label: t('auto.lbl.birthPlace') },
        { key: 'personal_id_number', label: t('auto.lbl.personalIdNumber') },
        { key: 'id_card_number', label: t('auto.lbl.idCardNumber') },
        { key: 'id_card_expiry_date', label: t('auto.lbl.idCardExpiry') },
        { key: 'passport_number', label: t('auto.lbl.passportNumber') },
        { key: 'passport_expiry_date', label: t('auto.lbl.passportExpiry') }
      ]
    },
    {
      title: t('auto.lbl.emergencyContact'),
      fields: [
        { key: 'emergency_contact_name', label: t('auto.lbl.contactName') },
        { key: 'emergency_contact_phone', label: t('auto.lbl.contactPhone') },
        { key: 'emergency_contact_email', label: t('auto.lbl.contactEmail') },
        { key: 'emergency_contact_relationship', label: t('auto.lbl.relationship') }
      ]
    },
    {
      title: t('auto.lbl.businessInformation'),
      fields: [
        { key: 'business_name', label: t('auto.lbl.businessName') },
        { key: 'tax_id_number', label: t('auto.lbl.taxIdNumber') },
        { key: 'pkd_number', label: t('auto.lbl.pkdNumber') }
      ]
    },
    {
      title: t('auto.lbl.visasPermits'),
      fields: [
        { key: 'eu_visa_type', label: t('auto.lbl.euVisaType') },
        { key: 'eu_visa_expiry_date', label: t('auto.lbl.euVisaExpiry') },
        { key: 'us_visa_type', label: t('auto.lbl.usVisaType') },
        { key: 'us_visa_expiry_date', label: t('auto.lbl.usVisaExpiry') },
        { key: 'uk_work_permit_type', label: t('auto.lbl.ukWorkPermitType') },
        { key: 'uk_work_permit_expiry_date', label: t('auto.lbl.ukWorkPermitExpiry') }
      ]
    },
    {
      title: t('auto.lbl.certificates'),
      fields: [
        { key: 'certificates', label: t('auto.lbl.certificatesSummary') }
      ]
    }
  ];

  const filteredCount = getFilteredEmployees().length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {t('auto.misc.customEmployeeReport')}
          </CardTitle>
          <CardDescription>
            {t('auto.misc.selectTheDataFieldsYouWant')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filters */}
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold">{t('auto.misc.filters')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>{t('auto.misc.statusFilter')}</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('auto.misc.allStatuses2')}</SelectItem>
                    <SelectItem value="active">{t('auto.misc.activeOnly')}</SelectItem>
                    <SelectItem value="not_active">{t('auto.misc.notActiveOnly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('auto.misc.departmentFilter')}</Label>
                <Select
                  value={filters.department}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, department: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('auto.misc.allDepartments')}</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <div className="text-sm">
                  <span className="font-semibold text-lg">{filteredCount}</span> employees will be included
                </div>
              </div>
            </div>

            {/* Employee Selection */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <Label>{t('auto.misc.selectSpecificEmployeesOptional')}</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleSelectAllEmployeesToggle}
                 data-testid="btn-selectallemployeestoggle">
                  {selectAllEmployees ? t('auto.misc.deselectAll') : t('auto.misc.selectAll')}
                </Button>
              </div>
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 bg-white">
                {getBaseFilteredEmployees().length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">{t('auto.misc.noEmployeesMatchTheFilters')}</p>
                ) : (
                  <div className="space-y-1">
                    {getBaseFilteredEmployees().map(emp => (
                      <div key={emp.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`emp-${emp.id}`}
                          checked={selectAllEmployees || filters.selectedEmployees.includes(emp.id)}
                          onCheckedChange={() => handleEmployeeToggle(emp.id)}
                        />
                        <Label htmlFor={`emp-${emp.id}`} className="text-sm cursor-pointer">
                          {emp.name || 'Unnamed'} {emp.department ? `(${emp.department})` : ''}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('auto.misc.leaveAllUncheckedToIncludeAll')}
              </p>
            </div>
          </div>

          {/* Field Selection */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">{t('auto.misc.selectFieldsToInclude')}</h3>
              <div className="space-x-2">
                <Button variant="outline" size="sm" onClick={handleSelectAll} data-testid="btn-select-all">
                  {t('auto.misc.selectAll')}
                </Button>
                <Button variant="outline" size="sm" onClick={handleSelectNone} data-testid="btn-select-none">
                  {t('auto.misc.selectNone')}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {fieldGroups.map(group => (
                <Card key={group.title}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">{group.title}</CardTitle>
                      <Checkbox
                        checked={isGroupSelected(group.fields)}
                        onCheckedChange={() => handleGroupToggle(group.fields)}
                        aria-label={`Select all ${group.title}`}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {group.fields.map(field => (
                      <div key={field.key} className="flex items-center space-x-2">
                        <Checkbox
                          id={field.key}
                          checked={selectedFields[field.key]}
                          onCheckedChange={() => handleFieldToggle(field.key)}
                        />
                        <Label
                          htmlFor={field.key}
                          className="text-sm cursor-pointer"
                        >
                          {field.label}
                        </Label>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <DocLangToggle testId="doc-lang-customemployeereport-0" />
            <Button
              onClick={() => generateReport('xlsx')}
              disabled={generating || filteredCount === 0}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {generating ? t('auto.misc.generating') : t('auto.misc.exportToExcel')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomEmployeeReport;
